<template>

<div class="login">

	<h1 class="login-heading">Sign in with geekgroup.app</h1>
	<p class="login-context" v-if="clientName">to continue to <b>{{ clientName }}</b></p>

	<form class="login-form" v-on:submit.prevent="primary">

		<div class="login-at-wrap">
			<span class="login-at">@</span>
			<input class="gg-field login-field--at" v-model="username" placeholder="your-bgg-username" autocomplete="username" spellcheck="false" :disabled="busy" />
		</div>

		<input v-if="mode === 'password'" class="gg-field" type="password" v-model="password" placeholder="geekgroup password" autocomplete="current-password" :disabled="busy" />

		<template v-else-if="codeStep === 'sent'">
			<input class="gg-field" v-model="code" inputmode="numeric" maxlength="6" placeholder="6-digit code" :disabled="busy" />
			<p class="login-hint">We mailed a code to your BGG geekmail.</p>
		</template>

		<p v-else class="login-hint">We’ll geekmail a 6-digit login code to your BGG account.</p>

		<p class="gg-error-text" v-if="error">{{ error }}</p>

		<button type="submit" class="gg-btn gg-btn--primary login-go" :disabled="!ready || busy">{{ primaryLabel }}</button>

	</form>

	<div class="login-switch">
		<button class="gg-link" type="button" v-if="mode === 'password'" v-on:click="toCode">Geekmail me a code instead</button>
		<template v-else>
			<button class="gg-link" type="button" v-if="codeStep === 'sent' && !busy" v-on:click="send">Resend code</button>
			<button class="gg-link" type="button" v-on:click="toPassword">Use a password instead</button>
		</template>
	</div>

	<button class="login-cancel" type="button" v-on:click="$emit('cancel')">Cancel</button>

</div>

</template>

<script>

import api from '../service/api'

/*
	geekgroup login — bgg username + geekgroup password, or a passwordless
	6-digit code geekmailed to the user's BGG account. On success emits 'authed'
	with the fresh token; 'cancel' returns the user to the client. Styled to
	match the main app's homepage sign-in.
*/
export default {

	name: 'Login',

	props: {
		clientName: { type: String, default: '' }
	},

	data: function() {

		return {
			mode: 'password',
			codeStep: 'idle',
			username: '',
			password: '',
			code: '',
			busy: false,
			error: ''
		}

	},

	computed: {

		ready: function() {

			if (!this.username.trim()) return false
			if (this.mode === 'password') return !!this.password
			if (this.codeStep === 'sent') return /^\d{6}$/.test(this.code.trim())

			return true

		},

		primaryLabel: function() {

			if (this.busy) return this.mode === 'password' ? 'Signing in…' : (this.codeStep === 'sent' ? 'Verifying…' : 'Sending…')
			if (this.mode === 'password') return 'Sign in'

			return this.codeStep === 'sent' ? 'Verify code' : 'Geekmail me a code'

		}

	},

	methods: {

		primary: function() {

			if (!this.ready || this.busy) return

			if (this.mode === 'password') return this.signin()
			if (this.codeStep === 'sent') return this.verify()

			this.send()

		},

		signin: function() {

			var self = this

			this.busy = true
			this.error = ''

			api.login(this.username.trim(), this.password).then(function(res) {

				self.busy = false

				if (res && res.token) return self.$emit('authed', res.token)

				self.error = 'That username or password wasn’t right. Try again, or geekmail yourself a code.'

			})

		},

		send: function() {

			var self = this

			this.busy = true
			this.error = ''

			api.sendCode(this.username.trim()).then(function(res) {

				self.busy = false

				if (res && res.kind === 'sent') { self.codeStep = 'sent'; return }

				self.error = self.codeMessage(res)

			})

		},

		verify: function() {

			var self = this

			this.busy = true
			this.error = ''

			api.verifyCode(this.username.trim(), this.code.trim()).then(function(res) {

				self.busy = false

				if (res && res.token) return self.$emit('authed', res.token)

				self.error = self.codeMessage(res)

			})

		},

		codeMessage: function(res) {

			var kind = res && res.kind

			if (kind === 'wrong_code') return 'That code wasn’t right. Check it and try again.'
			if (kind === 'code_expired') return 'That code has expired. Send a fresh one.'
			if (kind === 'rate_limited') return 'Too many tries. Give it a minute and try again.'
			if (kind === 'network' || (res && res.error === 'network')) return 'Couldn’t reach geekgroup. Check your connection.'

			return 'We couldn’t find that geekgroup user. Check the username and try again.'

		},

		toCode: function() { this.mode = 'code'; this.codeStep = 'idle'; this.error = ''; this.password = '' },

		toPassword: function() { this.mode = 'password'; this.codeStep = 'idle'; this.error = ''; this.code = '' }

	}

}

</script>

<style scoped>

.login {
	width: 380px;
	max-width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
}

/* keep the error from widening the column */
.login .gg-error-text {
	width: 100%;
	overflow-wrap: anywhere;
}

.login-heading {
	font-family: var(--sans);
	font-weight: 600;
	font-size: 19px;
	letter-spacing: -.2px;
	color: #fff;
	text-align: center;
}

.login-context {
	font-family: var(--sans);
	font-size: 13.5px;
	color: var(--muted);
	margin-top: 6px;
}

.login-context b { color: rgba(255, 255, 255, .85); font-weight: 600; }

.login-form {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 26px;
	text-align: left;
}

.login-at-wrap { position: relative; display: flex; align-items: center; }

.login-at {
	position: absolute;
	left: 14px;
	font-family: var(--mono);
	color: var(--faint);
	font-size: 16px;
	pointer-events: none;
}

.login-field--at { padding-left: 32px; }

.login-hint {
	font-family: var(--sans);
	font-size: 12px;
	color: var(--muted);
	line-height: 1.5;
	padding: 0 2px;
}

.login-go { margin-top: 8px; }

.login-switch {
	display: flex;
	justify-content: center;
	gap: 18px;
	margin-top: 18px;
}

.login-cancel {
	margin-top: 26px;
	background: none;
	border: none;
	color: rgba(255, 255, 255, .4);
	font-family: var(--sans);
	font-size: 13px;
	cursor: pointer;
}

.login-cancel:hover { color: rgba(255, 255, 255, .75); }

</style>
